import type {
  OperationalLoopRecipeWindow,
  OperationalLoopReport,
  OperationalLoopVerification,
  OptimizationMechanism,
} from "@/lib/api/building";
import { HELSINKI_TIME_ZONE } from "./operational-loop-data";

// Reading the operational-loop report's verification side at one grid index — the pure half of
// the cursor-following "Aktiivinen resepti" card and the "Verifiointi" strip. Every number here
// is read from the report (services/building/.../operational_loop.py computed the series, the
// windows, the strategy limits and the window summary); this module only looks them up at an
// index and applies the one shared rule, `followed`, the backend uses too. No euros — the
// three questions are answered in the recipe's own units (docs/spec/terminology.md →
// "Verified? Yes — checked against the outcome after apply").

export const MECHANISM_LABELS: Record<OptimizationMechanism, string> = {
  electricity_price: "Sähkön hinta",
  weather_forecast: "Sääennuste · aurinko",
  demand_response: "Kysyntäjousto",
  building_usage: "Käyttöaste-setback",
  building_dynamics: "Rakennusdynamiikka",
};

/** Display names of the fixed, universal loop signals (not KB-varying equipment ports). */
export const SIGNAL_LABELS: Record<string, string> = {
  electricity_price_eur_kwh: "Spot-hinta",
  outdoor_temperature_c: "Ulkolämpötila",
  solar_radiation_wm2: "Auringon säteily",
  occupancy: "Käyttöaste",
  co2: "CO₂",
  temperature: "Huonelämpötila",
};

const EXTERNAL_NAMES = [
  "electricity_price_eur_kwh",
  "outdoor_temperature_c",
  "solar_radiation_wm2",
];
const INTERNAL_NAMES = ["co2", "occupancy", "temperature"];

export type Focus = { external: Set<string>; internal: Set<string> };

/** Which of the charted signals the KB's optimization services actually read for this
 *  equipment type (`report.focus`, the union across every compatible service). `null` when the
 *  KB declares no focus at all — the charts then show everything rather than nothing. */
export function focusOf(report: OperationalLoopReport | null): Focus | null {
  if (!report || report.focus.length === 0) return null;
  const external = new Set(
    report.focus.filter((n) => EXTERNAL_NAMES.includes(n)),
  );
  const internal = new Set(
    report.focus.filter((n) => INTERNAL_NAMES.includes(n)),
  );
  if (external.size === 0 && internal.size === 0) return null;
  return { external, internal };
}

/** The recipe in force at a grid index, if any. */
export function windowAt(
  report: OperationalLoopReport,
  index: number,
): OperationalLoopRecipeWindow | null {
  return (
    report.recipe_windows.find(
      (w) => w.start_index <= index && index < w.end_index,
    ) ?? null
  );
}

/** The same parameter-free "ohjaus toteutui" rule the backend applies: the sensor ended up
 *  closer to what the recipe commanded than to what it was doing before (the held perusura);
 *  trivially true when no change was commanded. */
export function followed(
  commanded: number,
  measured: number,
  baseline: number,
): boolean {
  return (
    baseline === commanded ||
    Math.abs(measured - commanded) < Math.abs(measured - baseline)
  );
}

export type ControlReading = {
  parameter: string;
  label: string;
  unit: string | null;
  commanded: number | null;
  measured: number | null;
  baseline: number | null;
  /** null when any of the three is missing at this index — a gap, not a verdict. */
  followed: boolean | null;
};

/** Question 1 at one index: commanded vs. measured vs. perusura per written port. The commanded
 *  value follows Optimointiresepti: the bucketed optimized series at this index, falling back to
 *  the recipe window's last target only when that step has no series sample. Measured/baseline
 *  pair only when a held perusura series exists for the measured sensor on the same unit —
 *  offset-native ports (no KB `baseline_of`) leave both null. */
export function controlAt(
  report: OperationalLoopReport,
  window: OperationalLoopRecipeWindow | null,
  index: number,
): ControlReading[] {
  const byName = new Map(report.recipe.map((s) => [s.name, s]));
  const byRoleAndUnit = (role: string, unit: string | null) =>
    report.recipe.find((s) => s.role === role && s.unit === unit) ?? null;
  return report.recipe
    .filter((s) => s.role === "optimized")
    .map((optimized) => {
      const measuredCandidate = byRoleAndUnit("measured", optimized.unit);
      const baseline =
        measuredCandidate != null
          ? (byName.get(`${measuredCandidate.name}_held_baseline`) ?? null)
          : null;
      const measured =
        baseline != null && baseline.role === "baseline" ? measuredCandidate : null;
      const commanded =
        optimized.values[index] ?? window?.targets[optimized.name] ?? null;
      const m = measured?.values[index] ?? null;
      const b = baseline?.values[index] ?? null;
      return {
        parameter: optimized.name,
        label: optimized.label ?? optimized.name,
        unit: optimized.unit,
        commanded,
        measured: m,
        baseline: b,
        followed:
          commanded != null && m != null && b != null
            ? followed(commanded, m, b)
            : null,
      };
    });
}

export type ConstraintReading = {
  signal: string;
  label: string;
  comparator: "<=" | ">=";
  threshold: number;
  unit: string | null;
  /** The worst served-zone value at this index (max for "<=", min for ">="); null = no data. */
  worst: number | null;
  zoneName: string | null;
  ok: boolean | null;
};

/** Question 2 at one index: each strategy bound against the worst served zone. */
export function constraintsAt(
  report: OperationalLoopReport,
  index: number,
): ConstraintReading[] {
  return report.constraints.map((c) => {
    let worst: number | null = null;
    let zoneName: string | null = null;
    for (const zone of report.internal) {
      const value =
        zone.series.find((s) => s.name === c.signal)?.values[index] ?? null;
      if (value == null) continue;
      const isWorse =
        worst == null ||
        (c.comparator === "<=" ? value > worst : value < worst);
      if (isWorse) {
        worst = value;
        zoneName = zone.zone_name;
      }
    }
    return {
      signal: c.signal,
      label: SIGNAL_LABELS[c.signal] ?? c.signal,
      comparator: c.comparator,
      threshold: c.threshold,
      unit: c.unit,
      worst,
      zoneName,
      ok:
        worst == null
          ? null
          : c.comparator === "<="
            ? worst <= c.threshold
            : worst >= c.threshold,
    };
  });
}

/** Question 3 at one index: already-computed fan-electrical % vs perusura from `report.impact`. */
export function impactDeltaAt(
  report: OperationalLoopReport,
  index: number,
): number | null {
  const opt = (report.impact ?? []).find((s) => s.role === "optimized");
  const base = (report.impact ?? []).find((s) => s.role === "baseline");
  const o = opt?.values[index] ?? null;
  const b = base?.values[index] ?? null;
  if (o == null || b == null) return null;
  return o - b;
}

export function verificationRates(v: OperationalLoopVerification | null): {
  controlPct: number | null;
  constraintPct: number | null;
} {
  if (!v) return { controlPct: null, constraintPct: null };
  const pct = (part: number, whole: number) =>
    whole > 0 ? Math.round((part / whole) * 100) : null;
  return {
    controlPct: pct(v.control_followed_steps, v.control_checked_steps),
    constraintPct: pct(v.constraint_ok_steps, v.constraint_checked_steps),
  };
}

/** "HH.MM" (Europe/Helsinki) for a grid index. */
export function timeLabelAt(
  report: OperationalLoopReport,
  index: number,
): string {
  const at = new Date(
    new Date(report.window.anchor).getTime() +
      index * report.window.step_minutes * 60_000,
  );
  return at.toLocaleTimeString("fi-FI", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: HELSINKI_TIME_ZONE,
  });
}
